import { Button } from 'features/components/Button';
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow
} from 'features/components/Table';
import { useDispatch } from 'hooks/utils';
import { useLanguage } from 'features/hooks/useLanguage';
import { useUsersByIds } from 'hooks/store/data/accounts/useUsersByIds';
import React, { useEffect } from 'react';
import { getProjects } from 'store/data/projects';
import { LanguageType } from 'types/index';
import { ProjectInvitationStatus, useProjectInvitations } from './useProjectInvitations';
import { formatDate } from 'utils/date';

const DICTIONARY = {
	[LanguageType.English]: {
		loading: 'Loading invitations...',
		failedToLoad: 'Failed to load project invitations',
		projectInvitations: 'Project Invitations',
		manageInvitations: 'Manage all pending and historical project invitations',
		invitedBy: 'Invited By',
		status: 'Status',
		revokedBy: 'Revoked By',
		expirationDate: 'Expiration Date',
		actions: 'Actions',
		revoke: 'Revoke',
		accept: 'Accept',
		decline: 'Decline',
		showing: 'Showing',
		invitation: 'invitation',
		invitations: 'invitations',
		projectName: 'Project Name'
	},
	[LanguageType.Norwegian]: {
		loading: 'Laster invitasjoner...',
		failedToLoad: 'Kunne ikke laste prosjektinvitasjoner',
		projectInvitations: 'Prosjektinvitasjoner',
		manageInvitations: 'Administrer alle ventende og historiske prosjektinvitasjoner',
		invitedBy: 'Invitert av',
		status: 'Status',
		revokedBy: 'Tilbakekalt av',
		expirationDate: 'Utløpsdato',
		actions: 'Handlinger',
		revoke: 'Tilbakekall',
		accept: 'Godta',
		decline: 'Avslå',
		showing: 'Viser',
		invitation: 'invitasjon',
		invitations: 'invitasjoner',
		projectName: 'Prosjektnavn'
	}
};

export const ProjectInvitationsTableForUser: React.FC = () => {
	const {
		invitations,
		isLoading,
		isQueryError,
		rejectInvitation,
		acceptInvitation,
		rejectInvitationMutation,
		acceptInvitationMutation
	} = useProjectInvitations();

	const [usersById, getUsersByIds] = useUsersByIds();

	const language = useLanguage();
	const dictionary = DICTIONARY[language];
	const dispatch = useDispatch();

	// Fetch inviter information when invitations are loaded
	useEffect(() => {
		if (invitations && invitations.length > 0) {
			const inviterIds = [...new Set(invitations.map(inv => inv.inviterUserId))];
			getUsersByIds(inviterIds);
		}
	}, [invitations, getUsersByIds]);

	const renderInviterNameCell = (inviterUserId: string, inviterName: string) => {
		const userData = usersById[inviterUserId];

		// Check if we're still loading user data
		const hasInviterIds = invitations && invitations.length > 0;
		const isLoadingUserData = hasInviterIds && !userData && Object.keys(usersById).length === 0;

		if (isLoadingUserData) {
			return (
				<div className="flex flex-col animate-pulse">
					<div className="h-5 w-32 bg-gray-200 rounded"></div>
					<div className="h-4 w-24 bg-gray-200 rounded mt-1"></div>
				</div>
			);
		}

		if (userData) {
			return (
				<div className="flex flex-col">
					<span>{inviterName}</span>
					<span className="text-sm text-gray-500">{userData.emailAddress}</span>
				</div>
			);
		}

		// Fallback to just the name if user data is not available
		return inviterName;
	};

	const handleDeclineInvitation = async (invitationId: number) => {
		try {
			await rejectInvitation({ invitationId });
		} catch (error) {
			console.error('Failed to decline invitation:', error);
		}
	};

	const handleAcceptInvitation = async (invitationId: number) => {
		try {
			await acceptInvitation({ invitationId });
			dispatch(getProjects());
		} catch (error) {
			console.error('Failed to accept invitation:', error);
		}
	};

	const getStatusColor = (status: string) => {
		switch (status) {
			case ProjectInvitationStatus.PENDING:
				return 'text-yellow-600 bg-yellow-100 border border-yellow-200';
			case ProjectInvitationStatus.ACCEPTED:
				return 'text-green-600 bg-green-100 border border-green-200';
			case ProjectInvitationStatus.EXPIRED:
				return 'text-gray-600 bg-gray-100 border border-gray-200';
			case ProjectInvitationStatus.REVOKED:
				return 'text-red-600 bg-red-100 border border-red-200';
			case ProjectInvitationStatus.REJECTED:
				return 'text-red-600 bg-red-100 border border-red-200';
			default:
				return 'text-gray-600';
		}
	};

	if (isLoading) {
		return (
			<div className="w-full flex items-center justify-center p-10">
				<div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-500"></div>
				<p className="mt-4 text-gray-600 font-medium">{dictionary.loading}</p>
			</div>
		);
	}

	if (isQueryError) {
		return (
			<div className="w-full rounded-xl shadow-md p-5 bg-red-50 border border-red-200">
				<p className="text-red-600 font-medium text-center">{dictionary.failedToLoad}</p>
			</div>
		);
	}

	return (
		<div className="flex flex-col gap-4">
			<div>
				<span className="table-info">
					{dictionary.showing} {invitations?.length || 0}{' '}
					{invitations?.length !== 1 ? dictionary.invitations : dictionary.invitation}
				</span>
			</div>
			<Table>
				<TableHeader>
					<TableRow>
						<TableHead>
							<div className="table-header-label">{dictionary.invitedBy}</div>
						</TableHead>
						<TableHead>
							<div className="table-header-label">{dictionary.projectName}</div>
						</TableHead>
						<TableHead>
							<div className="table-header-label">{dictionary.status}</div>
						</TableHead>
						<TableHead>
							<div className="table-header-label">{dictionary.expirationDate}</div>
						</TableHead>
						<TableHead>
							<div className="table-header-label">{dictionary.actions}</div>
						</TableHead>
					</TableRow>
				</TableHeader>
				<TableBody>
					{invitations &&
						invitations.map(invitation => (
							<TableRow key={`${invitation.invitationId}`}>
								<TableCell>
									{renderInviterNameCell(
										invitation.inviterUserId,
										invitation.inviterNameAtTheTimeOfTheInvitation
									)}
								</TableCell>
								<TableCell>{invitation.projectNameAtTime}</TableCell>
								<TableCell>
									<span
										className={`px-3 py-1.5 text-xs rounded-full ${getStatusColor(
											invitation.status
										)} shadow-sm`}
									>
										{invitation.status}
									</span>
								</TableCell>
								<TableCell>{formatDate(invitation.expiresAt)}</TableCell>
								<TableCell>
									{invitation.status === ProjectInvitationStatus.PENDING && (
										<div className="flex items-center space-x-2">
											<Button
												className="table-cell-btn"
												variant="destructive"
												text={dictionary.decline}
												onClick={() =>
													handleDeclineInvitation(invitation.invitationId)
												}
												loading={
													rejectInvitationMutation.isLoading &&
													rejectInvitationMutation.variables
														?.invitationId === invitation.invitationId
												}
											/>
											<Button
												className="table-cell-btn"
												variant="primary"
												text={dictionary.accept}
												onClick={() =>
													handleAcceptInvitation(invitation.invitationId)
												}
												loading={
													acceptInvitationMutation.isLoading &&
													acceptInvitationMutation.variables
														?.invitationId === invitation.invitationId
												}
											/>
										</div>
									)}
								</TableCell>
							</TableRow>
						))}
				</TableBody>
			</Table>
		</div>
	);
};
